import type { IssuedPairingCode, TeamMember } from "@don/wire";

import { addFailedCopy, type AddForm } from "./add.js";
import type { ReactivateTarget } from "./reactivate.js";
import type { TeamSource } from "./team.js";
import { TeammateDialog } from "./TeammateDialog.js";

const EMPTY: AddForm = { name: "", email: "", worksIn: "", touched: { name: false, email: false }, onTeam: null };

/**
 * Add teammate (epic 26-10-02-knowledge-v0, issue 215; design-spec §5 `add-form`, `add-busy`,
 * `add-form-errors`, `action-failed`): the shared `TeammateDialog`, empty, titled "Add
 * teammate", Add / Adding…. A failed add names the person as typed.
 *
 * `onAdded` is called with the server's row and code; the page then opens the code panel. An
 * address on a deactivated person (`email_deactivated`, issue 218) shows the offer band, and
 * its button calls `onReactivate` with that person; the page then asks the Reactivate confirm.
 */
export function AddTeammateDialog({
  add, onAdded, onReactivate, onClose,
}: {
  add: TeamSource["add"];
  onAdded: (person: TeamMember, code: IssuedPairingCode) => void;
  onReactivate: (target: ReactivateTarget) => void;
  onClose: () => void;
}) {
  return (
    <TeammateDialog
      kind="add"
      title="Add teammate"
      initial={EMPTY}
      submitLabel="Add"
      busyLabel="Adding…"
      failedCopy={(sent) => addFailedCopy(sent.name)}
      send={async (request) => {
        const outcome = await add(request);
        switch (outcome.outcome) {
          case "added":
            onAdded(outcome.person, outcome.code);
            return "done";
          case "email_on_team":
            return "email_on_team";
          case "email_deactivated":
            return {
              emailDeactivated: {
                person_id: outcome.person_id,
                display_name: outcome.display_name,
                deactivated_at_ms: outcome.deactivated_at_ms,
              },
            };
        }
      }}
      onReactivate={onReactivate}
      onClose={onClose}
    />
  );
}
